import { ReactNode } from 'react';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { mapFormContentPaddingSx } from 'components/MapFormLayout';
import { useTabs } from 'hooks/useTabs';

// Pass the form's values type explicitly (<GeoEventFormTabs<MyFormValues> ...>) so that error checking works
type GeoEventFormTabsProps<TFormValues extends FieldValues> = {
  idPrefix: string;
  internalFieldNames: FieldPath<TFormValues>[];
  internalContent: ReactNode;
  children: ReactNode;
};

export const GeoEventFormTabs = <TFormValues extends FieldValues>(
  props: GeoEventFormTabsProps<TFormValues>,
): JSX.Element => {
  const { idPrefix, internalFieldNames, internalContent, children } = props;
  const { t } = useTranslation();
  const { trigger } = useFormContext<TFormValues>();

  const renderPanelContent = (
    header: string,
    content: ReactNode,
  ): ReactNode => (
    <>
      <Typography variant="h3">{header}</Typography>
      <Divider sx={{ mt: 1, mb: 3 }} />
      {content}
    </>
  );

  // The form only submits from the Public tab, so Internal fields must be
  // valid before the user can leave that tab.
  const canChangeTab = async (prevTabId: string | null): Promise<boolean> => {
    if (prevTabId !== 'internal') return true;

    const isValid = await trigger(internalFieldNames);
    if (!isValid) {
      enqueueSnackbar(t('geoEvent.formTabs.internalErrors'), {
        variant: 'error',
      });
    }
    return isValid;
  };

  // Panels stay mounted so the uncontrolled rich text editors keep their
  // content when switching tabs.
  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix,
    ariaLabel: t('geoEvent.formTabs.ariaLabel'),
    keepMounted: true,
    canChangeTab,
    tabData: [
      {
        tabId: 'public',
        label: t('geoEvent.tabs.public'),
        enabled: true,
        content: renderPanelContent(
          t('geoEvent.formTabs.publicHeader'),
          children,
        ),
        panelSx: mapFormContentPaddingSx,
      },
      {
        tabId: 'internal',
        label: t('geoEvent.tabs.internal'),
        enabled: true,
        content: renderPanelContent(
          t('geoEvent.formTabs.internalHeader'),
          internalContent,
        ),
        panelSx: mapFormContentPaddingSx,
      },
    ],
  });

  return (
    <>
      <Tabs {...tabsProps} sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
        {tabProps.map(({ key, ...rest }) => (
          <Tab key={key} {...rest} />
        ))}
      </Tabs>
      <Box sx={{ isolation: 'isolate' }}>{tabPanels}</Box>
    </>
  );
};
